import { supabase } from "@/integrations/supabase/client";
import type { ParticipationPaymentStatus } from "@/api/payments";
import { gpxReplaceUntil } from "@/api/gpx";
import {
  bestChatForLocation,
  fetchLocalCommunityChats,
} from "@/api/localCommunityChat";
import {
  isVirtualLocationId,
  locationOrVirtual,
  VIRTUAL_LOCATION,
} from "@/lib/virtual";

export interface UpcomingEvent {
  event_id: number;
  quizrun_id: number;
  quizrun_number: number;
  quizrun_title: string;
  language: string;
  date_start: string;
  date_finish: string;
  /** The parent quizrun's window — drives current/upcoming/past classification. */
  quizrun_date_start: string;
  quizrun_date_finish: string;
  /** Null for a virtual event; see isVirtualEvent. */
  location_id: number | null;
  city: string;
  country: string | null;
  timezone: string | null;
  location_name: string | null;
  location_link: string | null;
  route_link: string | null;
  chat_link: string | null;
  is_registered: boolean;
  /** Ticket price in minor units (pence). Null = free. */
  price_cents: number | null;
  /** ISO-4217 currency for the ticket price (lowercase, e.g. 'gbp', 'amd'). */
  price_currency?: string | null;
  /**
   * True when the caller has already submitted a result for this event. Such an
   * event has effectively been played, so the profile shows it under History
   * (marked in-progress while still ongoing) rather than as an actionable
   * "Current" registration panel.
   */
  has_result?: boolean;
}

export const isVirtualEvent = (e: UpcomingEvent): boolean =>
  isVirtualLocationId(e.location_id);

/**
 * True when the current moment falls within the parent quizrun's start/finish
 * window — i.e. the run is ongoing ("current"), as opposed to upcoming (not yet
 * started) or past (already finished). Classification is by quizrun dates, not
 * the individual event's date, so every event of a running quizrun counts as
 * current. Both bounds inclusive.
 */
export const isEventOngoing = (e: UpcomingEvent): boolean => {
  const start = new Date(e.quizrun_date_start).getTime();
  const finish = new Date(e.quizrun_date_finish).getTime();
  const now = Date.now();
  return start <= now && now <= finish;
};

/**
 * Fetch every event of a not-yet-finished quizrun (classified by quizrun
 * dates) with a flag indicating whether `personId` is already registered. Pass
 * `null` for signed-out users.
 */
export async function fetchUpcomingEventsForPerson(
  personId: number | null,
): Promise<UpcomingEvent[]> {
  const nowIso = new Date().toISOString();

  // Classification is by quizrun dates, so start from quizruns that haven't
  // finished yet, then pull their events. This keeps every event of a running
  // quizrun visible (as "current") even if that individual event's own date has
  // already passed.
  type QuizrunRow = {
    id: number;
    number: number;
    title: string;
    language: string;
    date_start: string;
    date_finish: string;
  };
  const { data: quizrunsRaw, error: quizrunsErr } = await supabase
    .from("v_quizrun")
    .select("id, number, title, language, date_start, date_finish")
    .gte("date_finish", nowIso)
    .order("date_start", { ascending: true });
  if (quizrunsErr) throw quizrunsErr;
  if (!quizrunsRaw || quizrunsRaw.length === 0) return [];
  const quizruns = quizrunsRaw as QuizrunRow[];
  const quizrunMap = new Map(quizruns.map((q) => [q.id, q]));

  type EventWithPrice = {
    id: number;
    quizrun_id: number;
    location_id: number | null;
    date_start: string;
    date_finish: string;
    price_cents: number | null;
    price_currency: string | null;
  };
  const { data: eventsRaw, error: eventsErr } = await supabase
    .from("v_event")
    .select(
      "id, quizrun_id, location_id, date_start, date_finish, price_cents, price_currency",
    )
    .in(
      "quizrun_id",
      quizruns.map((q) => q.id),
    )
    .order("date_start", { ascending: true });
  if (eventsErr) throw eventsErr;
  if (!eventsRaw || eventsRaw.length === 0) return [];
  const events = eventsRaw as EventWithPrice[];

  const [locationsRes, registeredIds, communityChats, resultEventIds] =
    await Promise.all([
      supabase
        .from("v_location")
        .select(
          "id, city, country, timezone, location_name, location_link, route_link",
        )
        .in("id", [
          ...new Set(
            events
              .map((e) => e.location_id)
              .filter((id): id is number => id != null),
          ),
        ]),
      fetchRegisteredEventIds(
        personId,
        events.map((e) => e.id),
      ),
      fetchLocalCommunityChats(),
      fetchEventIdsWithResultForPerson(
        personId,
        events.map((e) => e.id),
      ),
    ]);
  if (locationsRes.error) throw locationsRes.error;

  const locationMap = new Map((locationsRes.data ?? []).map((l) => [l.id, l]));

  return events.map((e) => {
    const q = quizrunMap.get(e.quizrun_id);
    const l = locationOrVirtual(locationMap, e.location_id);
    return {
      event_id: e.id,
      quizrun_id: e.quizrun_id,
      quizrun_number: q?.number ?? 0,
      quizrun_title: q?.title ?? "",
      language: q?.language ?? "",
      date_start: e.date_start,
      date_finish: e.date_finish,
      quizrun_date_start: q?.date_start ?? e.date_start,
      quizrun_date_finish: q?.date_finish ?? e.date_finish,
      location_id: e.location_id,
      city: l?.city ?? "",
      country: l?.country ?? null,
      timezone: l?.timezone ?? null,
      location_name: l?.location_name ?? null,
      location_link: l?.location_link ?? null,
      route_link: l?.route_link ?? null,
      // Only expose the community invite URL to people registered for this event.
      chat_link: registeredIds.has(e.id)
        ? bestChatForLocation(
            communityChats,
            l?.country ?? null,
            l?.city ?? null,
          )
        : null,
      is_registered: registeredIds.has(e.id),
      price_cents: e.price_cents ?? null,
      price_currency: e.price_currency ?? null,
      has_result: resultEventIds.has(e.id),
    };
  });
}

/** Minimal public event shape for the signed-out registration card. */
export interface PublicEvent {
  event_id: number;
  quizrun_number: number;
  date_start: string;
  city: string;
  country: string | null;
  is_virtual: boolean;
  timezone: string | null;
  price_cents: number | null;
  price_currency: string | null;
}

/**
 * Fetch a single event for a signed-out visitor. Reads only the anon-readable
 * views (v_event / v_quizrun / v_location bypass RLS), so it carries no
 * person-scoped data — just enough to render the guest registration card.
 * Returns null when the event doesn't exist.
 */
export async function fetchPublicEvent(
  eventId: number,
): Promise<PublicEvent | null> {
  const { data: event, error: eventErr } = await supabase
    .from("v_event")
    .select(
      "id, quizrun_id, location_id, date_start, price_cents, price_currency",
    )
    .eq("id", eventId)
    .maybeSingle();
  if (eventErr) throw eventErr;
  if (!event) return null;

  const [quizrunRes, locationRes] = await Promise.all([
    supabase
      .from("v_quizrun")
      .select("number")
      .eq("id", event.quizrun_id)
      .maybeSingle(),
    event.location_id == null
      ? { data: VIRTUAL_LOCATION, error: null }
      : supabase
          .from("v_location")
          .select("city, country, timezone")
          .eq("id", event.location_id)
          .maybeSingle(),
  ]);
  if (quizrunRes.error) throw quizrunRes.error;
  if (locationRes.error) throw locationRes.error;

  return {
    event_id: event.id,
    quizrun_number: quizrunRes.data?.number ?? 0,
    date_start: event.date_start,
    city: locationRes.data?.city ?? "",
    country: locationRes.data?.country ?? null,
    is_virtual: isVirtualLocationId(event.location_id),
    timezone: locationRes.data?.timezone ?? null,
    price_cents: event.price_cents ?? null,
    price_currency: event.price_currency ?? null,
  };
}

/** A past event or virtual run the person took part in, with their own state. */
export interface PastEvent {
  /** Null for a detached virtual run (played by game code, no real event). */
  event_id: number | null;
  quizrun_id: number;
  quizrun_number: number;
  quizrun_title: string;
  date_start: string;
  date_finish: string | null;
  city: string;
  country: string | null;
  /** The caller's participation (needed to start a payment / upload a track). */
  participation_id: number;
  /** Virtual runs carry no event, price, or payment. */
  is_virtual: boolean;
  /** True when the caller has already uploaded a GPX track for this run. */
  has_gpx: boolean;
  /** Until when the caller may replace their own track (quizrun end + 2 days). */
  gpx_replace_until: string | null;
  /** True when the member is comped/first-timer-free. */
  is_free: boolean;
  /** 'paid' when a completed payment exists; null otherwise (unpaid). */
  payment_status: ParticipationPaymentStatus;
  /** Ticket price in minor units (pence). Null/0 = free, nothing to pay. */
  price_cents: number | null;
  /** ISO-4217 currency for the ticket price (lowercase, e.g. 'gbp', 'amd'). */
  price_currency?: string | null;
  /**
   * True when the parent quizrun is happening right now (start ≤ now ≤ finish).
   * Such a run appears here because a result was already submitted, and is
   * flagged "In progress" rather than shown as a finished event.
   */
  is_ongoing?: boolean;
}

/**
 * Quizrun ids the signed-in caller has finished playing (final think-segment
 * submitted) as a virtual run. Such a run counts as "past" even while its event
 * window is still open. Gated server-side to the caller.
 */
async function fetchCompletedQuizrunIds(): Promise<Set<number>> {
  const { data, error } = await supabase.rpc(
    "get_my_completed_quizruns" as never,
  );
  if (error) throw error;
  return new Set((data ?? []) as number[]);
}

/**
 * Everything in the person's history: past runs (parent quizrun finished) plus
 * runs already played (a result was submitted, or a virtual run the caller has
 * finished) and detached virtual runs (no event, e.g. played by game code),
 * most recent first. Carries the caller's own payment status, event price, and
 * whether they have uploaded a GPX track. Empty for signed-out users.
 */
export async function fetchPastEventsForPerson(
  personId: number | null,
): Promise<PastEvent[]> {
  if (personId === null) return [];
  const now = Date.now();

  // The caller's participations; RLS keeps this to their own membership rows.
  // gpx_file_link is the caller's own uploaded track. Event details are read
  // from the v_event VIEW (never the base `event` table — RLS on that table is
  // admin-only for authenticated users, so a base-table join returns null and
  // future onsite registrations would be misclassified as detached past runs).
  // completedQuizrunIds lets a finished virtual run count as past before its
  // window closes.
  const [{ data, error }, completedQuizrunIds] = await Promise.all([
    supabase
      .from("participation_member")
      .select(
        "gpx_file_link, participation!inner(id, quizrun_id, event_id, format, created_at)",
      )
      .eq("person_id", personId),
    fetchCompletedQuizrunIds(),
  ]);
  if (error) throw error;

  type Row = {
    gpx_file_link: string | null;
    participation: {
      id: number;
      quizrun_id: number;
      event_id: number | null;
      format: "onsite" | "virtual";
      created_at: string;
    } | null;
  };
  type EventRow = {
    id: number;
    location_id: number | null;
    date_start: string;
    date_finish: string;
    price_cents: number | null;
    price_currency: string | null;
  };
  const allRows = ((data ?? []) as Row[]).filter(
    (r): r is Row & { participation: NonNullable<Row["participation"]> } =>
      r.participation != null,
  );
  if (allRows.length === 0) return [];

  // Look up the backing events via the view. Detached/virtual runs carry no
  // event_id and need no lookup.
  const eventIds = [
    ...new Set(
      allRows
        .map((r) => r.participation.event_id)
        .filter((id): id is number => id != null),
    ),
  ];
  const eventMap = new Map<number, EventRow>();
  if (eventIds.length > 0) {
    const { data: eventsData, error: eventsErr } = await supabase
      .from("v_event")
      .select(
        "id, location_id, date_start, date_finish, price_cents, price_currency",
      )
      .in("id", eventIds);
    if (eventsErr) throw eventsErr;
    for (const e of (eventsData ?? []) as EventRow[])
      eventMap.set(e.id, e);
  }

  // Quizrun windows drive current/upcoming/past classification and the
  // in-progress flag, so fetch them for every participation up front.
  type QuizrunDates = {
    id: number;
    number: number;
    title: string;
    date_start: string;
    date_finish: string;
  };
  const { data: quizrunsData, error: quizrunsErr } = await supabase
    .from("v_quizrun")
    .select("id, number, title, date_start, date_finish")
    .in("id", [...new Set(allRows.map((r) => r.participation.quizrun_id))]);
  if (quizrunsErr) throw quizrunsErr;
  const quizrunMap = new Map(
    ((quizrunsData ?? []) as QuizrunDates[]).map((q) => [q.id, q]),
  );

  // Keep only genuinely past runs: a virtual/detached run (no event) always
  // counts; an event-backed run counts once its quizrun has finished OR the
  // caller has already submitted a result for it (a played-but-still-ongoing
  // run belongs in History, flagged in-progress, not in the Current panels).
  // Classification is by the parent quizrun's dates, not the individual event's.
  const participationWithResult = await fetchParticipationIdsWithResult(
    allRows.map((r) => r.participation.id),
  );

  const rows = allRows.filter((r) => {
    const p = r.participation;
    // A detached run (no backing event, e.g. played by game code) is always
    // history.
    if (p.event_id == null) return true;
    const q = quizrunMap.get(p.quizrun_id);
    if (q == null) return false;
    // Parse to epoch rather than lexically compare ISO strings: DB timestamptz
    // serialises with +00:00 while toISOString() uses Z, so string < is unsafe.
    const finished = new Date(q.date_finish).getTime() < now;
    if (finished || participationWithResult.has(p.id)) return true;
    // Window still open, but the caller has finished their own virtual run.
    return p.format === "virtual" && completedQuizrunIds.has(p.quizrun_id);
  });
  if (rows.length === 0) return [];

  const { data: payData, error: payError } = await supabase
    .from("v_member_payment")
    .select("participation_id, is_free, payment_status")
    .eq("person_id", personId);
  if (payError) throw payError;
  const statusByParticipation = new Map<
    number,
    { is_free: boolean; payment_status: string | null }
  >(
    (
      (payData ?? []) as Array<{
        participation_id: number;
        is_free: boolean;
        payment_status: string | null;
      }>
    ).map((p) => [
      p.participation_id,
      { is_free: p.is_free ?? false, payment_status: p.payment_status },
    ]),
  );

  const locationIds = rows
    .map((r) =>
      r.participation.event_id != null
        ? eventMap.get(r.participation.event_id)?.location_id
        : undefined,
    )
    .filter((id): id is number => id != null);
  const locationMap = new Map<
    number,
    { id: number; city: string | null; country: string | null }
  >();
  if (locationIds.length > 0) {
    const { data: locationsData, error: locationsErr } = await supabase
      .from("v_location")
      .select("id, city, country")
      .in("id", [...new Set(locationIds)]);
    if (locationsErr) throw locationsErr;
    for (const l of locationsData ?? []) locationMap.set(l.id, l);
  }

  return (
    rows
      .map((r) => {
        const p = r.participation;
        const e = p.event_id != null ? eventMap.get(p.event_id) : undefined;
        const q = quizrunMap.get(p.quizrun_id);
        const l = e ? locationOrVirtual(locationMap, e.location_id) : undefined;
        const pay = statusByParticipation.get(p.id);
        return {
          event_id: e?.id ?? null,
          quizrun_id: p.quizrun_id,
          quizrun_number: q?.number ?? 0,
          quizrun_title: q?.title ?? "",
          // Virtual runs have no event date — fall back to when they played.
          date_start: e?.date_start ?? p.created_at,
          date_finish: e?.date_finish ?? null,
          city: l?.city ?? "",
          country: l?.country ?? null,
          participation_id: p.id,
          is_virtual: p.format === "virtual",
          has_gpx: r.gpx_file_link != null,
          gpx_replace_until: q ? gpxReplaceUntil(q.date_finish) : null,
          is_free: pay?.is_free ?? false,
          payment_status:
            (pay?.payment_status as ParticipationPaymentStatus) ?? null,
          price_cents: e?.price_cents ?? null,
          price_currency: e?.price_currency ?? null,
          is_ongoing:
            q != null &&
            new Date(q.date_start).getTime() <= now &&
            now <= new Date(q.date_finish).getTime(),
        };
      })
      // Most recent first.
      .sort((a, b) =>
        a.date_start > b.date_start ? -1 : a.date_start < b.date_start ? 1 : 0,
      )
  );
}

export interface QuizrunGroup {
  quizrun_number: number;
  quizrun_title: string;
  /** Earliest event start in the group, used for sorting and the "May 2026" label. */
  earliest_start: string;
  /** All non-virtual events in the group, sorted: registered first, then by date. */
  cities: UpcomingEvent[];
  /** Virtual events in this Quizrun (rendered separately at the bottom of the group). */
  virtual: UpcomingEvent[];
  /** True iff the user is registered for any event in this Quizrun. */
  is_registered: boolean;
  /** The single registered event in this group, if any. */
  registered_event: UpcomingEvent | null;
}

/**
 * Group events by quizrun_number, sorted chronologically. Within each group,
 * non-virtual cities come first (registered city first, then by date), and
 * virtual events are kept in a separate bucket for distinct rendering.
 * Pure function — exported for testability.
 */
export function groupUpcomingEventsByQuizrun(
  events: UpcomingEvent[],
): QuizrunGroup[] {
  const byNumber = new Map<number, UpcomingEvent[]>();
  for (const e of events) {
    const arr = byNumber.get(e.quizrun_number);
    if (arr) arr.push(e);
    else byNumber.set(e.quizrun_number, [e]);
  }
  const groups: QuizrunGroup[] = [];
  for (const [num, list] of byNumber) {
    const cities = list.filter((e) => !isVirtualEvent(e));
    const virtual = list.filter(isVirtualEvent);
    const registered = list.find((e) => e.is_registered) ?? null;
    cities.sort((a, b) => {
      if (a.is_registered !== b.is_registered) return a.is_registered ? -1 : 1;
      return a.date_start.localeCompare(b.date_start);
    });
    virtual.sort((a, b) => a.date_start.localeCompare(b.date_start));
    const earliest = list.reduce(
      (min, e) => (e.date_start < min ? e.date_start : min),
      list[0].date_start,
    );
    groups.push({
      quizrun_number: num,
      quizrun_title: list[0].quizrun_title,
      earliest_start: earliest,
      cities,
      virtual,
      is_registered: registered !== null,
      registered_event: registered,
    });
  }
  groups.sort((a, b) => a.earliest_start.localeCompare(b.earliest_start));
  return groups;
}

async function fetchRegisteredEventIds(
  personId: number | null,
  eventIds: number[],
): Promise<Set<number>> {
  if (personId === null || eventIds.length === 0) return new Set();
  const { data, error } = await supabase
    .from("participation_member")
    .select("participation!inner(event_id)")
    .eq("person_id", personId)
    .in("participation.event_id", eventIds);
  if (error) throw error;
  return new Set(
    (data ?? [])
      .map(
        (r) =>
          (r.participation as { event_id: number | null } | null)?.event_id ??
          null,
      )
      .filter((id): id is number => id !== null),
  );
}

/**
 * Of the given participations, the subset that already have a submitted result.
 * RLS scopes `result` to rows the caller may read.
 */
async function fetchParticipationIdsWithResult(
  ids: number[],
): Promise<Set<number>> {
  if (ids.length === 0) return new Set();
  const { data, error } = await supabase
    .from("result")
    .select("participation_id")
    .in("participation_id", ids);
  if (error) throw error;
  return new Set(
    ((data ?? []) as Array<{ participation_id: number }>).map(
      (r) => r.participation_id,
    ),
  );
}

/**
 * Of the given events, the subset the caller has already submitted a result for.
 * Used to move a played (but still open) event out of the "Current" panels and
 * into History. RLS scopes participation_member / result to the caller's rows.
 */
async function fetchEventIdsWithResultForPerson(
  personId: number | null,
  eventIds: number[],
): Promise<Set<number>> {
  if (personId === null || eventIds.length === 0) return new Set();
  const { data: memberRows, error } = await supabase
    .from("participation_member")
    .select("participation!inner(id, event_id)")
    .eq("person_id", personId)
    .in("participation.event_id", eventIds);
  if (error) throw error;
  const participationToEvent = new Map<number, number>();
  for (const r of (memberRows ?? []) as Array<{
    participation: { id: number; event_id: number | null } | null;
  }>) {
    const p = r.participation;
    if (p && p.event_id != null) participationToEvent.set(p.id, p.event_id);
  }
  if (participationToEvent.size === 0) return new Set();

  const withResult = await fetchParticipationIdsWithResult([
    ...participationToEvent.keys(),
  ]);
  const eventsWithResult = new Set<number>();
  for (const pid of withResult) {
    const eventId = participationToEvent.get(pid);
    if (eventId != null) eventsWithResult.add(eventId);
  }
  return eventsWithResult;
}
